import { User, UserProfile, TeacherInfo, StudentInfo } from '../../types/auth';
import initSqlJs from 'sql.js';

// SQLite Database Service sử dụng sql.js với file system
export class SQLiteDatabaseService {
  private static instance: SQLiteDatabaseService;
  private db: any = null;
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private dbPath: string = './data/vatli365.db';

  private constructor() {
    // Don't initialize in constructor to avoid blocking
  }

  public static getInstance(): SQLiteDatabaseService {
    if (!SQLiteDatabaseService.instance) {
      SQLiteDatabaseService.instance = new SQLiteDatabaseService();
    }
    return SQLiteDatabaseService.instance;
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    // If already initializing, wait for it to complete
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Start initialization and save the promise
    this.initializationPromise = this.initializeDatabase();
    await this.initializationPromise;
    this.initializationPromise = null;
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized || this.initializationPromise) {
      await this.initialize();
    }
  }

  private async loadSQLJS(): Promise<any> {
    return new Promise((resolve, reject) => {
      // Check if sql.js is already loaded
      if (typeof window !== 'undefined' && (window as any).initSqlJs) {
        resolve((window as any).initSqlJs);
        return;
      }

      // Create script tag to load sql.js
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.8.0/sql-wasm.js';
      script.onload = () => {
        if ((window as any).initSqlJs) {
          resolve((window as any).initSqlJs);
        } else {
          reject(new Error('Failed to load sql.js'));
        }
      };
      script.onerror = () => {
        reject(new Error('Failed to load sql.js script'));
      };

      document.head.appendChild(script);
    });
  }

  private async initializeDatabase(): Promise<void> {
    try {
      // Load sql.js library with proper configuration
      const initSqlJs = await this.loadSQLJS();

      // ✅ ENABLED: Try to load existing database from localStorage backup
      let existingData = null;
      try {
        const backupData = localStorage.getItem('vatli365_database');
        if (backupData) {
          // Convert base64 to Uint8Array in chunks to avoid stack overflow
          const binaryString = atob(backupData);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          existingData = bytes;
          console.log(`✅ Loaded existing database from localStorage (${bytes.length} bytes)`);
        } else {
          console.log('📦 No existing database in localStorage, will create new one');
        }
      } catch (error) {
        console.error('❌ Error loading database from localStorage:', error);
        console.log('📦 Creating new database');
      }

      // ⚠️ CONDITIONAL ENABLE: Only enable for Learning History if explicitly requested
      const enableLearningHistory = import.meta.env.VITE_ENABLE_LEARNING_HISTORY_DB === 'true';

      if (enableLearningHistory) {
        console.log('🎓 Enabling Learning History Database (Phase 1)...');
        const SQL = await initSqlJs({ locateFile: (file: string) => `https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.8.0/${file}` });

        // Load existing database or create new one
        this.db = new SQL.Database(existingData);

        // Ensure all tables exist (in case schema changed)
        this.createTables();

        // Mark as initialized BEFORE running any queries
        this.isInitialized = true;

        // Log database statistics
        try {
          const studyProgressCount = this.db.exec("SELECT COUNT(*) as count FROM study_progress")[0]?.values[0][0] || 0;
          const quizResultsCount = this.db.exec("SELECT COUNT(*) as count FROM quiz_results")[0]?.values[0][0] || 0;
          const achievementsCount = this.db.exec("SELECT COUNT(*) as count FROM achievements")[0]?.values[0][0] || 0;
          console.log(`✅ Learning History Database enabled - Study Progress: ${studyProgressCount}, Quiz Results: ${quizResultsCount}, Achievements: ${achievementsCount}`);
        } catch (err) {
          console.log('✅ Learning History Database enabled');
        }

        // Try to migrate localStorage users (only if needed)
        if (!existingData) {
          try {
            console.log('🔄 Running migration from localStorage...');
            await this.migrateLocalStorageToSQLite();
            // Only save after migration
            await this.saveDatabase();
          } catch (mErr) {
            console.warn('LocalStorage migration skipped:', mErr);
          }
        } else {
          // Database loaded from localStorage, no need to save again
          console.log('📦 Database loaded from existing data, skipping auto-save');
        }
      } else {
        // Keep SQLite DISABLED for production (use localStorage/WordPress)
        this.isInitialized = false;
        console.log('📦 Learning History Database disabled (using localStorage fallback)');
      }

      console.log('SQLite Database initialized successfully');
    } catch (error) {
      console.error('Error initializing SQLite database:', error);
      console.log('Falling back to localStorage mode');
      // Fallback to localStorage if SQLite fails
      this.isInitialized = false;
    }
  }

  private async saveDatabase(): Promise<void> {
    if (!this.db) return;

    try {
      const data = this.db.export();

      // Save to localStorage as backup
      const base64 = btoa(String.fromCharCode(...new Uint8Array(data)));
      localStorage.setItem('vatli365_db_backup', base64);

      // Also save to file system
      await this.saveToFileSystem();

      console.log('Database saved to localStorage backup and file system');
    } catch (error) {
      console.error('Error saving database:', error);
    }
  }

  private createTables(): void {
    if (!this.db) return;

    // Users table
    this.db.run(`
      CREATE TABLE IF NOT EXISTS users (
        id TEXT PRIMARY KEY,
        email TEXT UNIQUE NOT NULL,
        name TEXT NOT NULL,
        avatar TEXT,
        provider TEXT DEFAULT 'google',
        providerId TEXT,
        role TEXT DEFAULT 'student',
        permissions TEXT, -- JSON string
        createdAt TEXT NOT NULL,
        updatedAt TEXT NOT NULL,
        lastLoginAt TEXT,
        isActive INTEGER DEFAULT 1,
        isVerified INTEGER DEFAULT 0,
        teacherInfo TEXT, -- JSON string
        studentInfo TEXT  -- JSON string
      )
    `);

    // User profiles table
    this.db.run(`
      CREATE TABLE IF NOT EXISTS user_profiles (
        id TEXT PRIMARY KEY,
        userId TEXT NOT NULL,
        fullName TEXT,
        dateOfBirth TEXT,
        grade TEXT,
        school TEXT,
        phone TEXT,
        address TEXT,
        bio TEXT,
        preferences TEXT, -- JSON string
        createdAt TEXT NOT NULL,
        updatedAt TEXT NOT NULL,
        FOREIGN KEY (userId) REFERENCES users (id) ON DELETE CASCADE
      )
    `);

    // Study progress table
    this.db.run(`
      CREATE TABLE IF NOT EXISTS study_progress (
        id TEXT PRIMARY KEY,
        userId TEXT NOT NULL,
        lessonId TEXT NOT NULL,
        lessonTitle TEXT,
        progress REAL DEFAULT 0,
        timeSpent INTEGER DEFAULT 0,
        startedAt TEXT,
        lastAccessedAt TEXT,
        completedAt TEXT,
        status TEXT DEFAULT 'in_progress',
        bookmarks TEXT,
        notes TEXT,
        interactiveStats TEXT,
        createdAt TEXT NOT NULL,
        updatedAt TEXT NOT NULL,
        FOREIGN KEY (userId) REFERENCES users (id) ON DELETE CASCADE
      )
    `);

    // Quiz results table
    this.db.run(`
      CREATE TABLE IF NOT EXISTS quiz_results (
        id TEXT PRIMARY KEY,
        userId TEXT NOT NULL,
        quizId TEXT NOT NULL,
        lessonId TEXT,
        quizTitle TEXT NOT NULL,
        quizType TEXT DEFAULT 'lesson',
        score REAL NOT NULL,
        totalQuestions INTEGER NOT NULL,
        correctAnswers INTEGER NOT NULL,
        wrongAnswers INTEGER NOT NULL,
        skippedAnswers INTEGER DEFAULT 0,
        timeSpent INTEGER NOT NULL,
        timeLimitSeconds INTEGER,
        passed INTEGER DEFAULT 0,
        passingScore REAL DEFAULT 70,
        answers TEXT NOT NULL,
        metadata TEXT,
        attemptNumber INTEGER DEFAULT 1,
        createdAt TEXT NOT NULL,
        FOREIGN KEY (userId) REFERENCES users (id) ON DELETE CASCADE
      )
    `);

    // Study sessions table
    this.db.run(`
      CREATE TABLE IF NOT EXISTS study_sessions (
        id TEXT PRIMARY KEY,
        userId TEXT NOT NULL,
        sessionType TEXT NOT NULL,
        contentId TEXT,
        duration INTEGER NOT NULL,
        actions TEXT,
        deviceInfo TEXT,
        createdAt TEXT NOT NULL,
        endedAt TEXT,
        FOREIGN KEY (userId) REFERENCES users (id) ON DELETE CASCADE
      )
    `);

    // Achievements table
    this.db.run(`
      CREATE TABLE IF NOT EXISTS achievements (
        id TEXT PRIMARY KEY,
        userId TEXT NOT NULL,
        achievementType TEXT NOT NULL,
        title TEXT NOT NULL,
        description TEXT,
        icon TEXT,
        metadata TEXT,
        earnedAt TEXT NOT NULL,
        FOREIGN KEY (userId) REFERENCES users (id) ON DELETE CASCADE
      )
    `);

    // ✅ Flashcards table (for teachers)
    this.db.run(`
      CREATE TABLE IF NOT EXISTS flashcards (
        id TEXT PRIMARY KEY,
        authorId TEXT NOT NULL,
        title TEXT NOT NULL,
        front TEXT NOT NULL,
        back TEXT NOT NULL,
        lessonId TEXT,
        tags TEXT,
        difficulty TEXT DEFAULT 'medium',
        isPublic INTEGER DEFAULT 0,
        views INTEGER DEFAULT 0,
        likes INTEGER DEFAULT 0,
        createdAt TEXT NOT NULL,
        updatedAt TEXT NOT NULL,
        FOREIGN KEY (authorId) REFERENCES users (id) ON DELETE CASCADE
      )
    `);

    // ✅ Create indexes for better query performance
    this.db.run('CREATE INDEX IF NOT EXISTS idx_study_progress_userId ON study_progress(userId)');
    this.db.run('CREATE INDEX IF NOT EXISTS idx_study_progress_lessonId ON study_progress(lessonId)');
    this.db.run('CREATE INDEX IF NOT EXISTS idx_study_progress_status ON study_progress(status)');

    this.db.run('CREATE INDEX IF NOT EXISTS idx_quiz_results_userId ON quiz_results(userId)');
    this.db.run('CREATE INDEX IF NOT EXISTS idx_quiz_results_quizId ON quiz_results(quizId)');
    this.db.run('CREATE INDEX IF NOT EXISTS idx_quiz_results_lessonId ON quiz_results(lessonId)');
    this.db.run('CREATE INDEX IF NOT EXISTS idx_quiz_results_createdAt ON quiz_results(createdAt)');

    this.db.run('CREATE INDEX IF NOT EXISTS idx_study_sessions_userId ON study_sessions(userId)');
    this.db.run('CREATE INDEX IF NOT EXISTS idx_study_sessions_sessionType ON study_sessions(sessionType)');

    this.db.run('CREATE INDEX IF NOT EXISTS idx_achievements_userId ON achievements(userId)');
    this.db.run('CREATE INDEX IF NOT EXISTS idx_achievements_type ON achievements(achievementType)');

    console.log('✅ SQLite tables and indexes created successfully');

    // ✅ Run migrations for schema updates
    this.runMigrations();
  }

  private runMigrations(): void {
    try {
      // Migration 1: Add interactiveStats column to study_progress table
      const tableInfo = this.db.exec("PRAGMA table_info(study_progress)");
      const hasInteractiveStatsColumn = tableInfo[0]?.values.some(
        (row) => row[1] === 'interactiveStats'
      );

      if (!hasInteractiveStatsColumn) {
        console.log('🔄 Running migration: Adding interactiveStats column to study_progress table');
        this.db.run('ALTER TABLE study_progress ADD COLUMN interactiveStats TEXT');
        console.log('✅ Migration completed: interactiveStats column added');
      } else {
        console.log('✅ Migration skipped: interactiveStats column already exists');
      }

      // Migration 2: Add progressBreakdown column to study_progress table
      const hasProgressBreakdownColumn = tableInfo[0]?.values.some(
        (row) => row[1] === 'progressBreakdown'
      );

      if (!hasProgressBreakdownColumn) {
        console.log('🔄 Running migration: Adding progressBreakdown column to study_progress table');
        this.db.run('ALTER TABLE study_progress ADD COLUMN progressBreakdown TEXT');
        console.log('✅ Migration completed: progressBreakdown column added');
      } else {
        console.log('✅ Migration skipped: progressBreakdown column already exists');
      }

      // Migration 3: Add fullscreenExitCount column to quiz_results table (anti-cheat)
      const quizTableInfo = this.db.exec("PRAGMA table_info(quiz_results)");
      const hasFullscreenExitCountColumn = quizTableInfo[0]?.values.some(
        (row) => row[1] === 'fullscreenExitCount'
      );

      if (!hasFullscreenExitCountColumn) {
        console.log('🔄 Running migration: Adding fullscreenExitCount column to quiz_results table');
        this.db.run('ALTER TABLE quiz_results ADD COLUMN fullscreenExitCount INTEGER DEFAULT 0');
        console.log('✅ Migration completed: fullscreenExitCount column added');
      } else {
        console.log('✅ Migration skipped: fullscreenExitCount column already exists');
      }
    } catch (error) {
      console.error('❌ Error running migrations:', error);
    }
  }

  // User Management
  async createUser(userData: Partial<User>): Promise<User> {
    await this.ensureInitialized();

    if (!this.isInitialized) {
      console.log('SQLite not initialized, using localStorage fallback');
      return this.createUserInLocalStorage(userData);
    }

    const user: User = {
      id: this.generateId(),
      email: userData.email || '',
      name: userData.name || '',
      avatar: userData.avatar,
      provider: userData.provider || 'google',
      providerId: userData.providerId,
      role: userData.role || 'student',
      permissions: this.getDefaultPermissions(userData.role || 'student'),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastLoginAt: new Date(),
      isActive: true,
      isVerified: false,
      teacherInfo: userData.teacherInfo,
      studentInfo: userData.studentInfo,
    };

    const stmt = this.db.prepare(`
      INSERT INTO users (
        id, email, name, avatar, provider, providerId, role, permissions,
        createdAt, updatedAt, lastLoginAt, isActive, isVerified, teacherInfo, studentInfo
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `);

    stmt.run([
      user.id,
      user.email,
      user.name,
      user.avatar ?? null,
      user.provider,
      user.providerId ?? null,
      user.role,
      JSON.stringify(user.permissions),
      user.createdAt.toISOString(),
      user.updatedAt.toISOString(),
      user.lastLoginAt ? user.lastLoginAt.toISOString() : null,
      user.isActive ? 1 : 0,
      user.isVerified ? 1 : 0,
      user.teacherInfo ? JSON.stringify(user.teacherInfo) : null,
      user.studentInfo ? JSON.stringify(user.studentInfo) : null,
    ]);

    stmt.free();
    console.log('User created in SQLite:', user);

    // Auto-save to file system
    await this.saveToFileSystem();

    return user;
  }

  async getUserById(id: string): Promise<User | null> {
    await this.ensureInitialized();
    if (!this.isInitialized) {
      // ✅ Add localStorage fallback like getUserByEmail
      console.log('SQLite not initialized, using localStorage fallback for getUserById');
      return this.getUserByIdFromLocalStorage(id);
    }

    const stmt = this.db.prepare('SELECT * FROM users WHERE id = ?');
    stmt.bind([id]);

    let result = null;
    if (stmt.step()) {
      result = stmt.getAsObject();
    }

    stmt.free();

    if (!result) return null;

    return this.mapRowToUser(result);
  }

  async getUserByEmail(email: string): Promise<User | null> {
    await this.ensureInitialized();
    if (!this.isInitialized) {
      console.log('SQLite not initialized, using localStorage fallback');
      return this.getUserByEmailFromLocalStorage(email);
    }

    const stmt = this.db.prepare('SELECT * FROM users WHERE email = ?');
    stmt.bind([email]);

    let result = null;
    if (stmt.step()) {
      result = stmt.getAsObject();
    }

    stmt.free();

    if (!result) return null;

    return this.mapRowToUser(result);
  }

  async getAllUsers(): Promise<User[]> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) {
      console.log('SQLite not initialized, using localStorage fallback for getAllUsers');
      const users = this.getAllUsersFromLocalStorage();
      return users.map((userData: any) => this.parseUserFromLocalStorage(userData));
    }

    try {
      const stmt = this.db.prepare('SELECT * FROM users ORDER BY createdAt DESC');
      const results = stmt.all();
      stmt.free();

      return results.map((row: any) => this.mapRowToUser(row));
    } catch (error) {
      console.error('Error getting all users from SQLite, falling back to localStorage:', error);
      const users = this.getAllUsersFromLocalStorage();
      return users.map((userData: any) => this.parseUserFromLocalStorage(userData));
    }
  }

  async updateUser(id: string, updates: Partial<User>): Promise<User> {
    if (!this.isInitialized || !this.db) {
      console.log('SQLite not initialized, using localStorage fallback for user update');
      return this.updateUserInLocalStorage(id, updates);
    }

    try {
      const user = await this.getUserById(id);
      if (!user) {
        console.log('❌ User not found in SQLite, trying localStorage:', id);
        // Fallback to localStorage if user not in SQLite
        return this.updateUserInLocalStorage(id, updates);
      }

      const updatedUser: User = {
        ...user,
        ...updates,
        updatedAt: new Date(),
      };

      // Ensure permissions is always a valid JSON string
      const safePermissions = Array.isArray(updatedUser.permissions)
        ? updatedUser.permissions
        : this.getDefaultPermissions(updatedUser.role);

      const stmt = this.db.prepare(`
      UPDATE users SET
        email = ?, name = ?, avatar = ?, provider = ?, providerId = ?, role = ?,
        permissions = ?, updatedAt = ?, lastLoginAt = ?, isActive = ?, isVerified = ?,
        teacherInfo = ?, studentInfo = ?
      WHERE id = ?
    `);

      const bindValues = [
        updatedUser.email,
        updatedUser.name,
        updatedUser.avatar ?? null,
        updatedUser.provider,
        updatedUser.providerId ?? null,
        updatedUser.role,
        JSON.stringify(safePermissions),
        updatedUser.updatedAt.toISOString(),
        updatedUser.lastLoginAt ? updatedUser.lastLoginAt.toISOString() : null,
        updatedUser.isActive ? 1 : 0,
        updatedUser.isVerified ? 1 : 0,
        updatedUser.teacherInfo ? JSON.stringify(updatedUser.teacherInfo) : null,
        updatedUser.studentInfo ? JSON.stringify(updatedUser.studentInfo) : null,
        id,
      ];

      stmt.run(bindValues);

      stmt.free();
      console.log('✅ User updated in SQLite:', updatedUser);

      // ✅ SYNC: Update localStorage.vatli365_users
      try {
        this.updateUserInLocalStorage(id, updates);
        console.log('✅ Synced update to localStorage.vatli365_users');
      } catch (lsError) {
        console.warn('⚠️ Failed to sync to localStorage:', lsError);
      }

      // Auto-save to file system
      await this.saveToFileSystem();

      return updatedUser;
    } catch (error) {
      console.error('Error updating user in SQLite, falling back to localStorage:', error);
      return this.updateUserInLocalStorage(id, updates);
    }
  }

  async deleteUser(id: string): Promise<void> {
    if (!this.isInitialized || !this.db) {
      console.log('SQLite not initialized, using localStorage fallback for user deletion');
      this.deleteUserFromLocalStorage(id);
      return;
    }

    try {
      const stmt = this.db.prepare('DELETE FROM users WHERE id = ?');
      stmt.run([id]);
      stmt.free();

      console.log('✅ User deleted from SQLite:', id);

      // ✅ SYNC: Xóa khỏi localStorage.vatli365_users
      this.deleteUserFromLocalStorage(id);

      // Auto-save to file system
      await this.saveToFileSystem();
    } catch (error) {
      console.error('Error deleting user from SQLite, falling back to localStorage:', error);
      this.deleteUserFromLocalStorage(id);
    }
  }

  async updateUserRole(id: string, newRole: 'student' | 'teacher' | 'admin'): Promise<User> {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    const user = await this.getUserById(id);
    if (!user) {
      throw new Error('User not found');
    }

    const updatedUser: User = {
      ...user,
      role: newRole,
      permissions: this.getDefaultPermissions(newRole),
      updatedAt: new Date(),
    };

    const stmt = this.db.prepare('UPDATE users SET role = ?, permissions = ?, updatedAt = ? WHERE id = ?');
    stmt.run([newRole, JSON.stringify(updatedUser.permissions), updatedUser.updatedAt.toISOString(), id]);
    stmt.free();

    console.log('User role updated in SQLite:', updatedUser);

    // Auto-save to file system
    await this.saveToFileSystem();

    return updatedUser;
  }

  async approveTeacher(id: string, teacherInfo: any): Promise<User> {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    const user = await this.getUserById(id);
    if (!user) {
      throw new Error('User not found');
    }

    const updatedUser: User = {
      ...user,
      role: 'teacher',
      permissions: this.getDefaultPermissions('teacher'),
      teacherInfo,
      updatedAt: new Date(),
    };

    const stmt = this.db.prepare('UPDATE users SET role = ?, permissions = ?, teacherInfo = ?, updatedAt = ? WHERE id = ?');
    stmt.run(['teacher', JSON.stringify(updatedUser.permissions), JSON.stringify(teacherInfo), updatedUser.updatedAt.toISOString(), id]);
    stmt.free();

    console.log('Teacher approved in SQLite:', updatedUser);

    // Auto-save to file system
    await this.saveToFileSystem();

    return updatedUser;
  }

  async updateStudentInfo(id: string, studentInfo: any): Promise<User> {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    const user = await this.getUserById(id);
    if (!user) {
      throw new Error('User not found');
    }

    const updatedUser: User = {
      ...user,
      studentInfo,
      updatedAt: new Date(),
    };

    const stmt = this.db.prepare('UPDATE users SET studentInfo = ?, updatedAt = ? WHERE id = ?');
    stmt.run([JSON.stringify(studentInfo), updatedUser.updatedAt.toISOString(), id]);
    stmt.free();

    console.log('Student info updated in SQLite:', updatedUser);

    // Auto-save to file system
    await this.saveToFileSystem();

    return updatedUser;
  }

  async checkPermission(id: string, permission: string): Promise<boolean> {
    if (!this.isInitialized) {
      return false;
    }

    const user = await this.getUserById(id);
    if (!user) {
      return false;
    }

    return user.permissions.includes(permission);
  }

  // User Profile Management
  async createUserProfile(profileData: Partial<UserProfile>): Promise<UserProfile> {
    if (!this.isInitialized) {
      console.log('SQLite not initialized, using localStorage fallback for profile');
      return this.createUserProfileInLocalStorage(profileData);
    }

    const profile: UserProfile = {
      id: this.generateId(),
      userId: profileData.userId || '',
      fullName: profileData.fullName || '',
      dateOfBirth: profileData.dateOfBirth,
      grade: profileData.grade,
      school: profileData.school,
      phone: profileData.phone,
      address: profileData.address,
      bio: profileData.bio,
      preferences: profileData.preferences || {
        theme: 'light',
        language: 'vi',
        notifications: true,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const stmt = this.db.prepare(`
      INSERT INTO user_profiles (
        id, userId, fullName, dateOfBirth, grade, school, phone, address, bio, preferences,
        createdAt, updatedAt
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `);

    stmt.run([
      profile.id,
      profile.userId,
      profile.fullName,
      profile.dateOfBirth ?? null,
      profile.grade ?? null,
      profile.school ?? null,
      profile.phone ?? null,
      profile.address ?? null,
      profile.bio ?? null,
      JSON.stringify(profile.preferences),
      profile.createdAt.toISOString(),
      profile.updatedAt.toISOString(),
    ]);

    stmt.free();
    console.log('Profile created in SQLite:', profile);

    // Auto-save to file system
    await this.saveToFileSystem();

    return profile;
  }

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    if (!this.isInitialized) {
      console.log('SQLite not initialized, using localStorage fallback for profile');
      return this.getUserProfileFromLocalStorage(userId);
    }

    const stmt = this.db.prepare('SELECT * FROM user_profiles WHERE userId = ?');
    stmt.bind([userId]);

    let result = null;
    if (stmt.step()) {
      result = stmt.getAsObject();
    }

    stmt.free();

    if (!result) return null;

    return this.mapRowToProfile(result);
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile> {
    if (!this.isInitialized) {
      console.log('SQLite not initialized, using localStorage fallback for profile update');
      return this.updateUserProfileInLocalStorage(userId, updates);
    }

    const profile = await this.getUserProfile(userId);
    if (!profile) {
      throw new Error('User profile not found');
    }

    const updatedProfile: UserProfile = {
      ...profile,
      ...updates,
      updatedAt: new Date(),
    };

    const stmt = this.db.prepare(`
      UPDATE user_profiles SET
        fullName = ?, dateOfBirth = ?, grade = ?, school = ?, phone = ?, address = ?,
        bio = ?, preferences = ?, updatedAt = ?
      WHERE userId = ?
    `);

    stmt.run([
      updatedProfile.fullName,
      updatedProfile.dateOfBirth ?? null,
      updatedProfile.grade ?? null,
      updatedProfile.school ?? null,
      updatedProfile.phone ?? null,
      updatedProfile.address ?? null,
      updatedProfile.bio ?? null,
      JSON.stringify(updatedProfile.preferences),
      updatedProfile.updatedAt.toISOString(),
      userId,
    ]);

    stmt.free();
    console.log('Profile updated in SQLite:', updatedProfile);

    // Auto-save to file system
    await this.saveToFileSystem();

    return updatedProfile;
  }

  // Database Management - Save to file system
  async saveToFileSystem(): Promise<void> {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      const data = this.db.export();

      // Save to localStorage as primary storage
      // Convert Uint8Array to base64 in chunks to avoid call stack overflow
      const uint8Array = new Uint8Array(data);
      const chunkSize = 8192; // Process 8KB at a time
      let binary = '';

      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, Array.from(chunk));
      }

      const base64 = btoa(binary);
      localStorage.setItem('vatli365_database', base64);

      // ❌ DISABLED: Auto-download (annoying for users)
      // Uncomment below if you want manual download feature
      /*
      const blob = new Blob([data], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vatli365-database-${new Date().toISOString().split('T')[0]}.db`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      */

      console.log('✅ Database saved to localStorage');
    } catch (error) {
      console.error('Error saving database to file system:', error);
      throw error;
    }
  }

  async exportDatabase(): Promise<Uint8Array> {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    return this.db.export();
  }

  async importDatabase(data: Uint8Array): Promise<void> {
    try {
      const initSqlJs = await this.loadSQLJS();
      // Initialize SQL.js module and open database from provided data
      const SQL = await initSqlJs({ locateFile: (file: string) => `https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.8.0/${file}` });
      this.db = new SQL.Database(data);
      this.isInitialized = true;

      console.log('Database imported successfully');
    } catch (error) {
      console.error('Error importing database:', error);
      throw error;
    }
  }

  async createBackup(): Promise<Uint8Array> {
    return this.exportDatabase();
  }

  async restoreFromBackup(data: Uint8Array): Promise<void> {
    await this.importDatabase(data);
  }

  // Utility Methods
  private mapRowToUser(row: any): User {
    return {
      id: row.id,
      email: row.email,
      name: row.name,
      avatar: row.avatar,
      provider: row.provider,
      providerId: row.providerId,
      role: row.role,
      permissions: row.permissions ? JSON.parse(row.permissions) : [],
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt),
      lastLoginAt: row.lastLoginAt ? new Date(row.lastLoginAt) : null,
      isActive: Boolean(row.isActive),
      isVerified: Boolean(row.isVerified),
      teacherInfo: row.teacherInfo ? JSON.parse(row.teacherInfo) : undefined,
      studentInfo: row.studentInfo ? JSON.parse(row.studentInfo) : undefined,
    };
  }

  private mapRowToProfile(row: any): UserProfile {
    return {
      id: row.id,
      userId: row.userId,
      fullName: row.fullName,
      dateOfBirth: row.dateOfBirth,
      grade: row.grade,
      school: row.school,
      phone: row.phone,
      address: row.address,
      bio: row.bio,
      preferences: row.preferences ? JSON.parse(row.preferences) : {
        theme: 'light',
        language: 'vi',
        notifications: true,
      },
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt),
    };
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private getDefaultPermissions(role: 'student' | 'teacher' | 'admin'): string[] {
    const permissions = {
      student: [
        'lessons:read',
        'quizzes:read',
        'quizzes:submit',
        'profile:read',
        'profile:write',
        'progress:read',
      ],
      teacher: [
        'lessons:read',
        'lessons:write',
        'lessons:delete',
        'quizzes:read',
        'quizzes:write',
        'quizzes:delete',
        'students:read',
        'students:manage',
        'profile:read',
        'profile:write',
        'progress:read',
        'progress:manage',
        'reports:read',
        'reports:write',
      ],
      admin: [
        'lessons:read',
        'lessons:write',
        'lessons:delete',
        'quizzes:read',
        'quizzes:write',
        'quizzes:delete',
        'users:read',
        'users:write',
        'users:delete',
        'users:manage',
        'teachers:approve',
        'students:manage',
        'profile:read',
        'profile:write',
        'progress:read',
        'progress:manage',
        'reports:read',
        'reports:write',
        'system:manage',
      ],
    };

    return permissions[role] || permissions.student;
  }

  // Get database statistics
  async getDatabaseStats(): Promise<any> {
    await this.ensureInitialized();
    if (!this.isInitialized) {
      // Return localStorage stats
      const users = this.getAllUsersFromLocalStorage();
      return {
        users: users.length,
        profiles: users.length, // Assume 1 profile per user
        googleUsers: users.filter(u => u.provider === 'google').length,
        databaseSize: 0,
      };
    }

    const usersCount = this.db.exec('SELECT COUNT(*) as count FROM users')[0]?.values[0][0] || 0;
    const profilesCount = this.db.exec('SELECT COUNT(*) as count FROM user_profiles')[0]?.values[0][0] || 0;
    const googleUsersCount = this.db.exec('SELECT COUNT(*) as count FROM users WHERE provider = "google"')[0]?.values[0][0] || 0;

    return {
      users: usersCount,
      profiles: profilesCount,
      googleUsers: googleUsersCount,
      databaseSize: this.db.export().length,
    };
  }

  // LocalStorage Fallback Methods
  private createUserInLocalStorage(userData: Partial<User>): User {
    // ✅ Kiểm tra email đã tồn tại chưa
    const users = this.getAllUsersFromLocalStorage();
    const existingUser = users.find((u: User) =>
      u.email.toLowerCase() === userData.email?.toLowerCase()
    );

    if (existingUser) {
      console.warn('⚠️ User with email already exists in localStorage:', userData.email);
      throw new Error(`User với email ${userData.email} đã tồn tại`);
    }

    const user: User = {
      id: this.generateId(),
      email: userData.email || '',
      name: userData.name || '',
      avatar: userData.avatar,
      provider: userData.provider || 'google',
      providerId: userData.providerId,
      role: userData.role || 'student',
      permissions: this.getDefaultPermissions(userData.role || 'student'),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastLoginAt: new Date(),
      isActive: true,
      isVerified: false,
      teacherInfo: userData.teacherInfo,
      studentInfo: userData.studentInfo,
    };

    // Save to localStorage
    users.push(user);
    localStorage.setItem('vatli365_users', JSON.stringify(users));

    console.log('✅ User created in localStorage:', user);
    return user;
  }

  private getUserByEmailFromLocalStorage(email: string): User | null {
    const users = this.getAllUsersFromLocalStorage();
    const user = users.find((u: any) => u.email.toLowerCase() === email.toLowerCase());

    if (user) {
      console.log('✅ Found user in localStorage by email:', email);
    } else {
      console.log('❌ User not found in localStorage for email:', email);
    }

    return user ? this.parseUserFromLocalStorage(user) : null;
  }

  private getUserByIdFromLocalStorage(id: string): User | null {
    const users = this.getAllUsersFromLocalStorage();
    const user = users.find((u: any) => u.id === id);

    if (user) {
      console.log('✅ Found user in localStorage by id:', id);
    } else {
      console.log('❌ User not found in localStorage for id:', id);
    }

    return user ? this.parseUserFromLocalStorage(user) : null;
  }

  private getAllUsersFromLocalStorage(): any[] {
    const usersJson = localStorage.getItem('vatli365_users');
    return usersJson ? JSON.parse(usersJson) : [];
  }

  private parseUserFromLocalStorage(userData: any): User {
    return {
      ...userData,
      createdAt: new Date(userData.createdAt),
      updatedAt: new Date(userData.updatedAt),
      lastLoginAt: userData.lastLoginAt ? new Date(userData.lastLoginAt) : null,
    };
  }

  private updateUserInLocalStorage(id: string, updates: Partial<User>): User {
    const users = this.getAllUsersFromLocalStorage();
    const userIndex = users.findIndex(u => u.id === id);

    if (userIndex === -1) {
      throw new Error('User not found');
    }

    const user = this.parseUserFromLocalStorage(users[userIndex]);
    const updatedUser: User = {
      ...user,
      ...updates,
      updatedAt: new Date(),
    };

    // Update in localStorage
    users[userIndex] = updatedUser;
    localStorage.setItem('vatli365_users', JSON.stringify(users));

    console.log('User updated in localStorage:', updatedUser);
    return updatedUser;
  }

  private deleteUserFromLocalStorage(id: string): void {
    const users = this.getAllUsersFromLocalStorage();
    console.log('🗑️ [deleteUserFromLocalStorage] Total users before delete:', users.length);
    console.log('🗑️ [deleteUserFromLocalStorage] Looking for user with id:', id);

    const userToDelete = users.find(u => u.id === id);
    if (userToDelete) {
      console.log('🗑️ [deleteUserFromLocalStorage] Found user to delete:', userToDelete.email);
    } else {
      console.log('❌ [deleteUserFromLocalStorage] User not found with id:', id);
    }

    const filteredUsers = users.filter(u => u.id !== id);

    if (users.length === filteredUsers.length) {
      console.error('❌ [deleteUserFromLocalStorage] User not found in localStorage');
      throw new Error('User not found');
    }

    localStorage.setItem('vatli365_users', JSON.stringify(filteredUsers));
    console.log('✅ [deleteUserFromLocalStorage] User deleted from localStorage:', id);
    console.log('📊 [deleteUserFromLocalStorage] Remaining users:', filteredUsers.length);
    console.log('📊 [deleteUserFromLocalStorage] Remaining emails:', filteredUsers.map(u => u.email).join(', '));
  }

  // User Profile LocalStorage Fallback Methods
  private createUserProfileInLocalStorage(profileData: Partial<UserProfile>): UserProfile {
    const profile: UserProfile = {
      id: this.generateId(),
      userId: profileData.userId || '',
      fullName: profileData.fullName || '',
      dateOfBirth: profileData.dateOfBirth,
      grade: profileData.grade,
      school: profileData.school,
      phone: profileData.phone,
      address: profileData.address,
      bio: profileData.bio,
      preferences: profileData.preferences || {
        theme: 'light',
        language: 'vi',
        notifications: true,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Save to localStorage
    const profiles = this.getAllUserProfilesFromLocalStorage();
    profiles.push(profile);
    localStorage.setItem('vatli365_user_profiles', JSON.stringify(profiles));

    console.log('User profile created in localStorage:', profile);
    return profile;
  }

  private getUserProfileFromLocalStorage(userId: string): UserProfile | null {
    const profiles = this.getAllUserProfilesFromLocalStorage();
    const profile = profiles.find(p => p.userId === userId);
    return profile ? this.parseProfileFromLocalStorage(profile) : null;
  }

  private getAllUserProfilesFromLocalStorage(): any[] {
    const profilesJson = localStorage.getItem('vatli365_user_profiles');
    return profilesJson ? JSON.parse(profilesJson) : [];
  }

  private parseProfileFromLocalStorage(profileData: any): UserProfile {
    return {
      ...profileData,
      createdAt: new Date(profileData.createdAt),
      updatedAt: new Date(profileData.updatedAt),
    };
  }

  // Migrate users and profiles from localStorage to SQLite (idempotent)
  private async migrateLocalStorageToSQLite(): Promise<void> {
    if (!this.isInitialized) return;

    try {
      const lsUsers = this.getAllUsersFromLocalStorage();
      if (lsUsers.length === 0) return;

      // For each user, upsert into SQLite
      for (const raw of lsUsers) {
        const user = this.parseUserFromLocalStorage(raw);

        // Check existence by email
        const checkStmt = this.db.prepare('SELECT id FROM users WHERE email = ?');
        checkStmt.bind([user.email]);

        let existing = null;
        if (checkStmt.step()) {
          existing = checkStmt.getAsObject();
        }

        checkStmt.free();

        if (!existing || !existing.id) {
          const insertStmt = this.db.prepare(`
            INSERT INTO users (
              id, email, name, avatar, provider, providerId, role, permissions,
              createdAt, updatedAt, lastLoginAt, isActive, isVerified, teacherInfo, studentInfo
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
          `);
          insertStmt.run([
            user.id,
            user.email,
            user.name,
            user.avatar ?? null,
            user.provider,
            user.providerId ?? null,
            user.role,
            JSON.stringify(Array.isArray(user.permissions) ? user.permissions : this.getDefaultPermissions(user.role)),
            user.createdAt ? new Date(user.createdAt).toISOString() : new Date().toISOString(),
            user.updatedAt ? new Date(user.updatedAt).toISOString() : new Date().toISOString(),
            user.lastLoginAt ? new Date(user.lastLoginAt).toISOString() : null,
            user.isActive ? 1 : 0,
            user.isVerified ? 1 : 0,
            user.teacherInfo ? JSON.stringify(user.teacherInfo) : null,
            user.studentInfo ? JSON.stringify(user.studentInfo) : null,
          ]);
          insertStmt.free();
        }
      }

      // Migrate profiles
      const lsProfiles = this.getAllUserProfilesFromLocalStorage();
      for (const raw of lsProfiles) {
        const profile = this.parseProfileFromLocalStorage(raw);
        const checkStmt = this.db.prepare('SELECT id FROM user_profiles WHERE userId = ?');
        checkStmt.bind([profile.userId]);

        let existing = null;
        if (checkStmt.step()) {
          existing = checkStmt.getAsObject();
        }

        checkStmt.free();

        if (!existing || !existing.id) {
          const insertStmt = this.db.prepare(`
            INSERT INTO user_profiles (
              id, userId, fullName, dateOfBirth, grade, school, phone, address, bio, preferences,
              createdAt, updatedAt
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
          `);
          insertStmt.run([
            profile.id,
            profile.userId,
            profile.fullName,
            profile.dateOfBirth ?? null,
            profile.grade ?? null,
            profile.school ?? null,
            profile.phone ?? null,
            profile.address ?? null,
            profile.bio ?? null,
            JSON.stringify(profile.preferences || { theme: 'light', language: 'vi', notifications: true }),
            profile.createdAt ? new Date(profile.createdAt).toISOString() : new Date().toISOString(),
            profile.updatedAt ? new Date(profile.updatedAt).toISOString() : new Date().toISOString(),
          ]);
          insertStmt.free();
        }
      }

      await this.saveToFileSystem();
      console.log('LocalStorage data migrated to SQLite successfully');
    } catch (err) {
      console.warn('Migration to SQLite failed:', err);
    }
  }
  private updateUserProfileInLocalStorage(userId: string, updates: Partial<UserProfile>): UserProfile {
    const profiles = this.getAllUserProfilesFromLocalStorage();
    const profileIndex = profiles.findIndex(p => p.userId === userId);

    if (profileIndex === -1) {
      throw new Error('User profile not found');
    }

    const profile = this.parseProfileFromLocalStorage(profiles[profileIndex]);
    const updatedProfile: UserProfile = {
      ...profile,
      ...updates,
      updatedAt: new Date(),
    };

    // Update in localStorage
    profiles[profileIndex] = updatedProfile;
    localStorage.setItem('vatli365_user_profiles', JSON.stringify(profiles));

    console.log('User profile updated in localStorage:', updatedProfile);
    return updatedProfile;
  }

  // Study Progress LocalStorage Fallback Methods
  private createStudyProgressInLocalStorage(data: any): any {
    const progressList = this.getAllStudyProgressFromLocalStorageInternal();

    const now = new Date().toISOString();
    const newEntry = {
      ...data,
      id: this.generateId(),
      progress: data.progress || 0,
      timeSpent: data.timeSpent || 0,
      startedAt: data.startedAt || now,
      lastAccessedAt: now,
      status: data.status || 'in_progress',
      bookmarks: data.bookmarks || [],
      notes: data.notes || null,
      interactiveStats: data.interactiveStats || null,
      progressBreakdown: data.progressBreakdown || null,
      createdAt: now,
      updatedAt: now
    };

    progressList.push(newEntry);
    localStorage.setItem('vatli365_study_progress', JSON.stringify(progressList));

    return newEntry;
  }

  private getStudyProgressFromLocalStorage(userId: string, lessonId?: string): any {
    const progressList = this.getAllStudyProgressFromLocalStorageInternal();
    if (lessonId) {
      return progressList.find((p: any) => p.userId === userId && p.lessonId === lessonId) || null;
    }
    return progressList.find((p: any) => p.userId === userId) || null;
  }

  private getAllStudyProgressFromLocalStorage(userId: string): any[] {
    const progressList = this.getAllStudyProgressFromLocalStorageInternal();
    return progressList.filter((p: any) => p.userId === userId).sort((a: any, b: any) =>
      new Date(b.lastAccessedAt).getTime() - new Date(a.lastAccessedAt).getTime()
    );
  }

  private updateStudyProgressInLocalStorage(userId: string, lessonId: string, updates: any): void {
    const progressList = this.getAllStudyProgressFromLocalStorageInternal();
    const idx = progressList.findIndex((p: any) => p.userId === userId && p.lessonId === lessonId);

    if (idx !== -1) {
      const now = new Date().toISOString();
      const entry = progressList[idx];
      const updatedEntry = { ...entry, ...updates, updatedAt: now, lastAccessedAt: now };
      progressList[idx] = updatedEntry;
      localStorage.setItem('vatli365_study_progress', JSON.stringify(progressList));
    }
  }

  private getAllStudyProgressFromLocalStorageInternal(): any[] {
    const json = localStorage.getItem('vatli365_study_progress');
    return json ? JSON.parse(json) : [];
  }

  private startStudySessionInLocalStorage(session: any): string {
    const sessions = this.getStudySessionsFromLocalStorageInternal();
    const id = this.generateId();
    const now = new Date().toISOString();

    const newSession = {
      id,
      userId: session.userId,
      sessionType: session.sessionType,
      contentId: session.contentId || null,
      duration: 0,
      actions: [],
      deviceInfo: session.deviceInfo || null,
      createdAt: now,
      endedAt: null
    };

    sessions.push(newSession);
    localStorage.setItem('vatli365_study_sessions', JSON.stringify(sessions));
    return id;
  }

  private endStudySessionInLocalStorage(sessionId: string, duration: number, actions?: any[]): void {
    const sessions = this.getStudySessionsFromLocalStorageInternal();
    const idx = sessions.findIndex((s: any) => s.id === sessionId);
    if (idx !== -1) {
      sessions[idx].duration = duration;
      sessions[idx].endedAt = new Date().toISOString();
      if (actions) sessions[idx].actions = actions;
      localStorage.setItem('vatli365_study_sessions', JSON.stringify(sessions));
    }
  }

  private getStudySessionsFromLocalStorageInternal(): any[] {
    const json = localStorage.getItem('vatli365_study_sessions');
    return json ? JSON.parse(json) : [];
  }

  // Generic CRUD methods
  public async add<T>(table: string, data: T): Promise<void> {
    await this.ensureInitialized();
    // Implementation for adding data
    console.log(`Adding to ${table}:`, data);
  }

  public async get<T>(table: string, id: string): Promise<T | null> {
    await this.ensureInitialized();
    // Implementation for getting data
    console.log(`Getting from ${table} with id:`, id);
    return null;
  }

  public async update<T>(table: string, data: T): Promise<void> {
    await this.ensureInitialized();
    // Implementation for updating data
    console.log(`Updating ${table}:`, data);
  }

  public async delete(table: string, id: string): Promise<void> {
    await this.ensureInitialized();
    // Implementation for deleting data
    console.log(`Deleting from ${table} with id:`, id);
  }

  public async getAll<T>(table: string): Promise<T[]> {
    await this.ensureInitialized();
    // Implementation for getting all data
    console.log(`Getting all from ${table}`);
    return [];
  }

  // ==================== STUDY PROGRESS METHODS ====================

  public async createStudyProgress(data: any): Promise<any> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) {
      console.log('SQLite not initialized, using localStorage fallback for study progress');
      return this.createStudyProgressInLocalStorage(data);
    }

    const now = new Date().toISOString();
    const id = this.generateId();

    const stmt = this.db.prepare(`
      INSERT INTO study_progress (
        id, userId, lessonId, lessonTitle, progress, timeSpent,
        startedAt, lastAccessedAt, status, bookmarks, notes,
        createdAt, updatedAt
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `);

    stmt.run([
      id,
      data.userId,
      data.lessonId,
      data.lessonTitle || '',
      data.progress || 0,
      data.timeSpent || 0,
      data.startedAt || now,
      now,
      data.status || 'in_progress',
      JSON.stringify(data.bookmarks || []),
      data.notes || null,
      now,
      now
    ]);

    stmt.free();
    await this.saveToFileSystem();

    return { id, ...data, createdAt: now, updatedAt: now };
  }

  public async getStudyProgress(userId: string, lessonId?: string): Promise<any> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) {
      return this.getStudyProgressFromLocalStorage(userId, lessonId);
    }

    let query = 'SELECT * FROM study_progress WHERE userId = ?';
    const params: any[] = [userId];

    if (lessonId) {
      query += ' AND lessonId = ?';
      params.push(lessonId);
    }

    const stmt = this.db.prepare(query);
    stmt.bind(params);

    let result = null;
    if (stmt.step()) {
      result = stmt.getAsObject();
    }

    stmt.free();

    if (!result) return null;

    return this.mapStudyProgressRow(result);
  }

  public async getAllStudyProgress(userId: string): Promise<any[]> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) {
      return this.getAllStudyProgressFromLocalStorage(userId);
    }

    const stmt = this.db.prepare('SELECT * FROM study_progress WHERE userId = ? ORDER BY lastAccessedAt DESC');
    const results: any[] = [];

    stmt.bind([userId]);
    while (stmt.step()) {
      const row = stmt.getAsObject();
      results.push(this.mapStudyProgressRow(row));
    }

    stmt.free();
    return results;
  }

  public async updateStudyProgress(userId: string, lessonId: string, updates: any): Promise<void> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) {
      return this.updateStudyProgressInLocalStorage(userId, lessonId, updates);
    }

    const now = new Date().toISOString();

    // Build dynamic UPDATE query
    const fields: string[] = [];
    const values: any[] = [];

    if (updates.progress !== undefined) {
      fields.push('progress = ?');
      values.push(updates.progress);
    }
    if (updates.timeSpent !== undefined) {
      fields.push('timeSpent = ?');
      values.push(updates.timeSpent);
    }
    if (updates.status) {
      fields.push('status = ?');
      values.push(updates.status);
    }
    if (updates.completedAt) {
      fields.push('completedAt = ?');
      values.push(updates.completedAt);
    }
    if (updates.bookmarks) {
      fields.push('bookmarks = ?');
      values.push(JSON.stringify(updates.bookmarks));
    }
    if (updates.notes !== undefined) {
      fields.push('notes = ?');
      values.push(updates.notes);
    }
    if (updates.interactiveStats !== undefined) {
      fields.push('interactiveStats = ?');
      values.push(JSON.stringify(updates.interactiveStats));
    }
    if (updates.progressBreakdown !== undefined) {
      fields.push('progressBreakdown = ?');
      values.push(JSON.stringify(updates.progressBreakdown));
    }

    fields.push('lastAccessedAt = ?', 'updatedAt = ?');
    values.push(now, now, userId, lessonId);

    const query = `UPDATE study_progress SET ${fields.join(', ')} WHERE userId = ? AND lessonId = ?`;

    const stmt = this.db.prepare(query);
    stmt.run(values);
    stmt.free();

    await this.saveToFileSystem();
  }

  private mapStudyProgressRow(row: any): any {
    return {
      id: row.id,
      userId: row.userId,
      lessonId: row.lessonId,
      lessonTitle: row.lessonTitle,
      progress: row.progress,
      timeSpent: row.timeSpent,
      startedAt: row.startedAt,
      lastAccessedAt: row.lastAccessedAt,
      completedAt: row.completedAt,
      status: row.status,
      bookmarks: row.bookmarks ? JSON.parse(row.bookmarks) : [],
      notes: row.notes,
      interactiveStats: row.interactiveStats ? JSON.parse(row.interactiveStats) : null,
      progressBreakdown: row.progressBreakdown ? JSON.parse(row.progressBreakdown) : null,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt
    };
  }

  // ==================== QUIZ RESULTS METHODS ====================

  public async saveQuizResult(result: any): Promise<any> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) {
      throw new Error('Database not initialized');
    }

    const now = new Date().toISOString();
    const id = this.generateId();

    const stmt = this.db.prepare(`
      INSERT INTO quiz_results (
        id, userId, quizId, lessonId, quizTitle, quizType,
        score, totalQuestions, correctAnswers, wrongAnswers, skippedAnswers,
        timeSpent, timeLimitSeconds, passed, passingScore,
        answers, metadata, attemptNumber, fullscreenExitCount, createdAt
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `);

    stmt.run([
      id,
      result.userId,
      result.quizId,
      result.lessonId || null,
      result.quizTitle,
      result.quizType || 'lesson',
      result.score,
      result.totalQuestions,
      result.correctAnswers,
      result.wrongAnswers,
      result.skippedAnswers || 0,
      result.timeSpent,
      result.timeLimitSeconds || null,
      result.passed ? 1 : 0,
      result.passingScore || 70,
      JSON.stringify(result.answers),
      result.metadata ? JSON.stringify(result.metadata) : null,
      result.attemptNumber || 1,
      result.fullscreenExitCount || 0,
      now
    ]);

    stmt.free();
    await this.saveToFileSystem();

    console.log('✅ Quiz result saved:', { id, quizId: result.quizId, score: result.score });
    return { id, ...result, createdAt: now };
  }

  public async getQuizResults(userId: string, filters?: any): Promise<any[]> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) return [];

    let query = 'SELECT * FROM quiz_results WHERE userId = ?';
    const params: any[] = [userId];

    if (filters?.lessonId) {
      query += ' AND lessonId = ?';
      params.push(filters.lessonId);
    }
    if (filters?.quizId) {
      query += ' AND quizId = ?';
      params.push(filters.quizId);
    }
    if (filters?.passed !== undefined) {
      query += ' AND passed = ?';
      params.push(filters.passed ? 1 : 0);
    }

    query += ' ORDER BY createdAt DESC';

    if (filters?.limit) {
      query += ' LIMIT ?';
      params.push(filters.limit);
    }

    const stmt = this.db.prepare(query);
    const results: any[] = [];

    stmt.bind(params);
    while (stmt.step()) {
      const row = stmt.getAsObject();
      results.push(this.mapQuizResultRow(row));
    }

    stmt.free();
    return results;
  }

  public async getLatestQuizAttempt(userId: string, quizId: string): Promise<any> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) return null;

    const stmt = this.db.prepare(`
      SELECT * FROM quiz_results 
      WHERE userId = ? AND quizId = ? 
      ORDER BY createdAt DESC 
      LIMIT 1
    `);
    stmt.bind([userId, quizId]);

    let result = null;
    if (stmt.step()) {
      result = stmt.getAsObject();
    }

    stmt.free();

    if (!result) return null;
    return this.mapQuizResultRow(result);
  }

  private mapQuizResultRow(row: any): any {
    return {
      id: row.id,
      userId: row.userId,
      quizId: row.quizId,
      lessonId: row.lessonId,
      quizTitle: row.quizTitle,
      quizType: row.quizType,
      score: row.score,
      totalQuestions: row.totalQuestions,
      correctAnswers: row.correctAnswers,
      wrongAnswers: row.wrongAnswers,
      skippedAnswers: row.skippedAnswers,
      timeSpent: row.timeSpent,
      timeLimitSeconds: row.timeLimitSeconds,
      passed: row.passed === 1,
      passingScore: row.passingScore,
      answers: row.answers ? JSON.parse(row.answers) : [],
      metadata: row.metadata ? JSON.parse(row.metadata) : null,
      attemptNumber: row.attemptNumber,
      fullscreenExitCount: row.fullscreenExitCount || 0,
      createdAt: row.createdAt
    };
  }

  // ==================== STUDY SESSIONS METHODS ====================

  public async startStudySession(session: any): Promise<string> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) {
      return this.startStudySessionInLocalStorage(session);
    }

    const now = new Date().toISOString();
    const id = this.generateId();

    const stmt = this.db.prepare(`
      INSERT INTO study_sessions (
        id, userId, sessionType, contentId, duration,
        actions, deviceInfo, createdAt
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?)
    `);

    stmt.run([
      id,
      session.userId,
      session.sessionType,
      session.contentId || null,
      0, // duration starts at 0
      JSON.stringify([]),
      session.deviceInfo ? JSON.stringify(session.deviceInfo) : null,
      now
    ]);

    stmt.free();
    await this.saveToFileSystem();

    console.log('✅ Study session started:', id);
    return id;
  }

  public async endStudySession(sessionId: string, duration: number, actions?: any[]): Promise<void> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) {
      return this.endStudySessionInLocalStorage(sessionId, duration, actions);
    }

    const now = new Date().toISOString();

    const stmt = this.db.prepare(`
      UPDATE study_sessions 
      SET duration = ?, endedAt = ?, actions = ?
      WHERE id = ?
    `);

    stmt.run([
      duration,
      now,
      JSON.stringify(actions || []),
      sessionId
    ]);

    stmt.free();
    await this.saveToFileSystem();

    console.log('✅ Study session ended:', sessionId);
  }

  public async getRecentSessions(userId: string, limit: number = 10): Promise<any[]> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) return [];

    const stmt = this.db.prepare(`
      SELECT * FROM study_sessions 
      WHERE userId = ? 
      ORDER BY createdAt DESC 
      LIMIT ?
    `);

    const results: any[] = [];
    stmt.bind([userId, limit]);

    while (stmt.step()) {
      const row = stmt.getAsObject();
      results.push(this.mapSessionRow(row));
    }

    stmt.free();
    return results;
  }

  private mapSessionRow(row: any): any {
    return {
      id: row.id,
      userId: row.userId,
      sessionType: row.sessionType,
      contentId: row.contentId,
      duration: row.duration,
      actions: row.actions ? JSON.parse(row.actions) : [],
      deviceInfo: row.deviceInfo ? JSON.parse(row.deviceInfo) : null,
      createdAt: row.createdAt,
      endedAt: row.endedAt
    };
  }

  // ==================== ACHIEVEMENTS METHODS ====================

  public async createAchievement(achievement: any): Promise<any> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) {
      throw new Error('Database not initialized');
    }

    const now = new Date().toISOString();
    const id = this.generateId();

    const stmt = this.db.prepare(`
      INSERT INTO achievements (
        id, userId, achievementType, title, description,
        icon, metadata, earnedAt
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?)
    `);

    stmt.run([
      id,
      achievement.userId,
      achievement.achievementType,
      achievement.title,
      achievement.description || '',
      achievement.icon || '🏆',
      achievement.metadata ? JSON.stringify(achievement.metadata) : null,
      now
    ]);

    stmt.free();
    await this.saveToFileSystem();

    console.log('🏆 Achievement unlocked:', { id, type: achievement.achievementType });
    return { id, ...achievement, earnedAt: now };
  }

  public async getUserAchievements(userId: string): Promise<any[]> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) return [];

    const stmt = this.db.prepare(`
      SELECT * FROM achievements 
      WHERE userId = ? 
      ORDER BY earnedAt DESC
    `);

    const results: any[] = [];
    stmt.bind([userId]);

    while (stmt.step()) {
      const row = stmt.getAsObject();
      results.push(this.mapAchievementRow(row));
    }

    stmt.free();
    return results;
  }

  public async hasAchievement(userId: string, achievementType: string): Promise<boolean> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) return false;

    const stmt = this.db.prepare(`
      SELECT COUNT(*) as count FROM achievements 
      WHERE userId = ? AND achievementType = ?
    `);
    stmt.bind([userId, achievementType]);

    let result = null;
    if (stmt.step()) {
      result = stmt.getAsObject();
    }

    stmt.free();

    return result && (result as any).count > 0;
  }

  private mapAchievementRow(row: any): any {
    return {
      id: row.id,
      userId: row.userId,
      achievementType: row.achievementType,
      title: row.title,
      description: row.description,
      icon: row.icon,
      metadata: row.metadata ? JSON.parse(row.metadata) : null,
      earnedAt: row.earnedAt
    };
  }

  // ==================== DATABASE MANAGEMENT ====================

  public async clearDatabase(): Promise<void> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) return;

    // Clear all tables
    this.db.run('DELETE FROM study_progress');
    this.db.run('DELETE FROM quiz_results');
    this.db.run('DELETE FROM study_sessions');
    this.db.run('DELETE FROM achievements');

    await this.saveToFileSystem();
    console.log('🗑️ Database cleared');
  }

  public async exportData(): Promise<any> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) return {};

    // Export all data
    return {
      users: await this.getAllUsers(),
      studyProgress: [], // Add export logic if needed
      quizResults: [],
      studySessions: [],
      achievements: []
    };
  }

  public async importData(data: any): Promise<void> {
    await this.ensureInitialized();
    if (!this.isInitialized || !this.db) return;

    console.log('📥 Importing data...', data);
    // Add import logic if needed
  }
}

export const sqliteDatabaseService = SQLiteDatabaseService.getInstance();
